import {
  MorphemeTilePresentationProvider,
  useMorphemeTilePresentation,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { hyphenateSync } from "@-/com.npmjs.hyphen/en-us";
import { cn } from "@-/com.npmjs.cn";
import { Platform, Text, View } from "@-/com.npmjs.react-native";
import { styled } from "styled-components/native";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { morphemeGlossEmphasisClassName } from "#morpheme-gloss-emphasis-class-name";

const GLOSS_LINE_COUNT = 2;
const GLOSS_WORD_BREAK = /[\s-]/u;

// Gloss ("two") scales with the enclosing card's inline-size. On web we still
// use `cqi` so the gloss tracks the card's container width; on native we
// compute the font size from the card's pixel `size` prop because the
// `clamp(.., 8cqi, ..)` declaration drops silently and collapses the gloss to
// zero height (verified via inspect-element on iPhone 16e — same root cause as
// the CardDetailsTileUi edge-label fix).
const GLOSS_RATIO = 0.08;
const GLOSS_MIN_PX = 12;
const GLOSS_MAX_PX = 28;
const GLOSS_LINE_HEIGHT_RATIO = 1.2;

function getGlossFontSize(size: number): number {
  return Math.max(GLOSS_MIN_PX, Math.min(GLOSS_MAX_PX, size * GLOSS_RATIO));
}

// `line-height` is set in pixels rather than the unitless `1.2` the web build
// uses because React Native interprets unitless line-height as literal pixels
// rather than the CSS multiplier, which would crush the text to ~1px tall.
// Font weight is applied via className so unique / mnemonic / regular glosses
// can each pick the right emphasis.
const WebGlossText = withUniwind(styled.Text`
  align-self: stretch;
  text-align: center;
  font-size: clamp(12px, 8cqi, 28px);
  line-height: 1.2;
  hyphens: manual;
`);

function MorphemeDetailsTileGlossSectionContent({
  graphemeCard,
  size,
}: {
  graphemeCard: MorphemeDetailsTileSlice;
  size: number;
}) {
  const { getGraphemePrevalence } = useMorphemeTilePresentation();
  const studyPinyinList =
    getGraphemePrevalence(graphemeCard.front).asCharacter === "obscure"
      ? []
      : graphemeCard.pinyinList;
  // We intentionally use || here to handle empty strings
  const gloss = graphemeCard.back || studyPinyinList[0]?.gloss || "-";
  // Phrases already have useful breaks: keep "regard" and "going" intact in
  // "with regard to" and "smooth-going". Only hyphenate standalone words.
  const wrappingGloss = GLOSS_WORD_BREAK.test(gloss) ? gloss : hyphenateSync(gloss);
  const isFaded =
    graphemeCard.morphemeType === "grapheme" &&
    (graphemeCard.label === "archaic" || graphemeCard.obsolete === true);
  const glossClassName = cn(
    isFaded ? "text-muted-foreground" : "text-foreground",
    morphemeGlossEmphasisClassName(graphemeCard),
  );

  return (
    <View className="min-h-0 min-w-0 flex-1 items-center justify-center self-stretch overflow-hidden px-1">
      {Platform.OS === "web" ? (
        <WebGlossText
          accessibilityLabel={gloss}
          android_hyphenationFrequency="none"
          className={glossClassName}
          ellipsizeMode="tail"
          lineBreakStrategyIOS="standard"
          numberOfLines={GLOSS_LINE_COUNT}
        >
          {wrappingGloss}
        </WebGlossText>
      ) : (
        <Text
          accessibilityLabel={gloss}
          android_hyphenationFrequency="none"
          className={cn(glossClassName, "self-stretch text-center")}
          ellipsizeMode="tail"
          lineBreakStrategyIOS="standard"
          numberOfLines={GLOSS_LINE_COUNT}
          style={{
            fontSize: getGlossFontSize(size),
            lineHeight: getGlossFontSize(size) * GLOSS_LINE_HEIGHT_RATIO,
          }}
        >
          {wrappingGloss}
        </Text>
      )}
    </View>
  );
}

export function MorphemeDetailsTileGlossSection(
  props: React.ComponentProps<typeof MorphemeDetailsTileGlossSectionContent>,
) {
  return (
    <MorphemeTilePresentationProvider>
      <MorphemeDetailsTileGlossSectionContent {...props} />
    </MorphemeTilePresentationProvider>
  );
}
